import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// components
import { TextComponent } from 'baseComponents/TextComponent';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { ControlledInputComponent } from 'baseComponents/ControlledInputComponent';

// helpers
import {
  LoginFormValues,
  loginFormSchema,
} from 'helpers/formsValidationSchemas/loginScreen';

// store
import { useAuthStoreActions } from 'store/AuthStore';

// hooks
import { LoginResponseType, useLogin } from 'hooks/useLogin';

// constants
import { LOGIN_SCREEN } from 'constants/enums/login-screen';

import { AuthPageWrapper } from 'components/layouts/AuthPageWrapper';
import { Flex, notification } from 'antd';
import { getAuthErrorMessage } from 'helpers/get-auth-error-message';
import { useGetSsoAuthUrl } from 'hooks/useGetSsoAuthIUrl';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';

export const LoginScreen: React.FC = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<LoginFormValues>({
    reValidateMode: 'onSubmit',
    resolver: yupResolver(loginFormSchema),
  });
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTokens, setLoggedIn } = useAuthStoreActions();

  useEffect(() => {
    if (location.state === 'error') {
      return api.error({
        message: 'Something went wrong',
        description: `Something went wrong with login using Azure AD. Please try again later`,
        duration: 3,
      });
    } // eslint-disable-next-line
  }, []);

  const handleSuccessLogin = (data: LoginResponseType) => {
    setTokens({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    setLoggedIn(true);

    reset();

    const redirectTo = location.state?.from?.pathname || '/home';
    navigate(redirectTo, { replace: true });
  };

  const { mutate: loginUser } = useLogin({
    onSuccess: handleSuccessLogin,
    onError: (e) => {
      return api.error({
        message: 'Unable to sign in',
        description:
          e.response?.status === 401
            ? 'The email or password you entered is incorrect. Please try again.'
            : getAuthErrorMessage(e) ||
              'Something went wrong with login. Please try again later.',
        duration: 3,
      });
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    loginUser({ email: data.email, password: data.password });
  };

  const {
    isLoading: isSsoAuthUrlLoading,
    // refetch: getSsoAuthUrl
  } = useGetSsoAuthUrl();

  // const ssoLogin = () => {
  //   getSsoAuthUrl().then((res) => {
  //     if (res?.data?.url) {
  //       window.location.href = res?.data?.url;
  //     } else {
  //       return api.error({
  //         message: 'Something went wrong',
  //         description:
  //           'Something went wrong with Azure AD login. Please try again later.',
  //         duration: 3,
  //       });
  //     }
  //   });
  // };

  return (
    <AuthPageWrapper
      title={
        <>
          <TextComponent
            size='middle'
            styleType='secondary'
            text={LOGIN_SCREEN.WELCOME_BACK}
          />
          <TextComponent size='title-size' text={LOGIN_SCREEN.SIGN_IN} inline />
        </>
      }
    >
      {contextHolder}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex vertical gap={20}>
          <ControlledInputComponent
            labelValue={LOGIN_SCREEN.EMAIL}
            name='email'
            control={control}
            rules={{ required: LOGIN_SCREEN.EMAIL_REQUIRED }}
            type='text'
            error={errors.email?.message}
          />
          <ControlledInputComponent
            labelValue={LOGIN_SCREEN.PASSWORD}
            name='password'
            control={control}
            rules={{ required: LOGIN_SCREEN.PASSWORD_REQUIRED }}
            type='password'
            linkLabel={LOGIN_SCREEN.FORGOT_PASSWORD}
            linkTo='/forgot-password'
            error={errors.password?.message}
          />
          <span />
          <ButtonComponent
            appereance='primary'
            size='large'
            label={LOGIN_SCREEN.SIGN_IN}
            uppercase
            fullWidth
            htmlType='submit'
          />
          {/* TODO: uncomment once the SSO should go to prod  */}
          {/* <ButtonComponent
            appereance='secondary'
            size='large'
            label={'Sign in with Azure AD'}
            uppercase
            fullWidth
            onClick={ssoLogin}
          /> */}
        </Flex>
      </form>
      <FullScreenLoaderComponent spinning={isSsoAuthUrlLoading} />
    </AuthPageWrapper>
  );
};
